import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { UserCircleIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { cn } from '../../../lib/utils';
import { Block } from '../types';
import { BlockList } from './blocks/BlockList';
import LoadingState from '../../../components/primitives/LoadingState';

interface ChatMessageProps {
  role: 'user' | 'assistant';
  content: string;
  blocks?: Block[];
  isStreaming?: boolean;
  shouldStream?: boolean;
  toolStatus?: string | null;
  onRetry?: () => void;
  onStreamDone?: () => void;
}

/*** Detects whether a string or code block represents an AI-generated ASCII chart/plot.*/
function isAsciiChart(text: string): boolean {
  if (!text) return false;
  const verticalAxisMatches = text.match(/\b\d+\s*\|/g);
  if (verticalAxisMatches && verticalAxisMatches.length >= 2) return true;
  if (/(\+|\^|\|)\s*[-—=]{3,}/.test(text)) return true;
  if (/(\||█|#){4,}/.test(text) && /\d+/.test(text)) return true;
  return false;
}

/*** Strips markdown tables from text when an interactive table block is already present.*/
function stripMarkdownTables(text: string): string {
  if (!text) return text;

  const lines = text.split('\n');
  const isDelimiterRow = (line: string) =>
    /^[ \t]*\|?(\s*:?-{2,}:?\s*\|)+\s*:?-{2,}:?\s*\|?[ \t]*$/.test(line);

  const isTableRow = (line: string) => {
    const trimmed = line.trim();
    if (!trimmed) return false;
    return (
      (trimmed.startsWith('|') && trimmed.endsWith('|') && trimmed.length > 1) ||
      (trimmed.includes('|') && trimmed.split('|').length >= 3)
    );
  };

  const removeIndices = new Set<number>();

  for (let i = 0; i < lines.length; i++) {
    if (isDelimiterRow(lines[i])) {
      let start = i;
      while (start > 0 && isTableRow(lines[start - 1])) start--;
      let end = i;
      while (end + 1 < lines.length && isTableRow(lines[end + 1])) end++;
      for (let k = start; k <= end; k++) removeIndices.add(k);
    }
  }

  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();
    if (trimmed.startsWith('|') && trimmed.includes('|') && trimmed.split('|').length >= 3) {
      removeIndices.add(i);
    }
  }

  const filteredLines = lines.filter((_, idx) => !removeIndices.has(idx));
  return filteredLines.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

/*** Strips bulleted key-value shipment details from text when a shipment card block is present.*/
function stripShipmentDetailBullets(text: string): string {
  if (!text) return text;

  const lines = text.split('\n');
  const shipmentFieldRegex =
    /^[ \t]*(?:[-*•]|\d+\.)\s*(?:\*\*)?(?:CN(?:\s*number)?|Consignment(?:\s*note)?|Customer[- ]facing status|Shipment status|Status|CN date|EDD|Expected delivery(?:\s*date)?|Delivery date|ETA|Route|Origin|Destination|Pincode|Distance(?:[\s\w/]*)|Remaining(?:[\s\w/]*)|Load type|Trip type|Actual(?:[\s\w/]*)weight|Weight|Charged? weight|E-waybill(?:[\s\w]*)|Invoice(?:[\s\w]*)|Consignor|Consignee|Party|Hold(?:[\s\w]*)|POD(?:[\s\w]*))\b(?:\*\*)?\s*:\s*.*$/i;

  return lines.filter((line) => !shipmentFieldRegex.test(line)).join('\n').trim();
}

/*** Strips raw share URLs, markdown links, and redundant share labels from message text when a dedicated ShareLink block is already rendered.*/
function stripShareLinks(text: string): string {
  if (!text) return text;

  // 1. Strip lines that are just labels or bullets pointing to share/public tracking links
  let cleaned = text.replace(/^[ \t]*(?:[-*•]|\d+\.)?\s*(?:(?:Here is (?:the|your) )?(?:\*\*)?(?:Share(?:able)? Link|Public (?:tracking )?link|Tracking link)\b(?:\*\*)?)\s*:\s*(?:\[[^\]]*\]\([^)]+\)|https?:\/\/[^\s]+|\/shipments\/[^\s]+)?\s*$/gim, '');

  // 2. Strip markdown links containing /shipments/track/public, token=, or /shipments/.../share
  cleaned = cleaned.replace(/\[([^\]]*)\]\((?:https?:\/\/[^\s)]+)?\/shipments\/(?:track\/public|[^)\s/]+\/share)[^)]*\)/gi, '');
  cleaned = cleaned.replace(/\[([^\]]*)\]\([^)]*token=[^)]*\)/gi, '');

  // 3. Strip bare URLs containing /shipments/track/public or /shipments/.../share
  cleaned = cleaned.replace(/(?:https?:\/\/[^\s<]+)?\/shipments\/(?:track\/public|[^)\s/]+\/share)(?:\?[^\s<.,;:)\]]*)?/gi, '');

  // 4. Strip standalone lead-in phrases if the link was next to it or on the next line
  cleaned = cleaned.replace(/^[ \t]*(?:[-*•]|\d+\.)?\s*(?:(?:You can )?track (?:it|your shipment) here|Here is (?:the|your) (?:public )?(?:tracking |shareable )?link)\s*:?\s*$/gim, '');
  cleaned = cleaned.replace(/(?:Share(?:able)? Link|Public (?:tracking )?link|Tracking link)\s*:\s*/gi, '');

  return cleaned.replace(/\n{3,}/g, '\n\n').trim();
}

/*** Cleans synthetic prompt residues, redundant bullets, duplicate markdown tables, and unrendered ASCII charts.*/
function cleanMessageContent(text: string, hasTableBlock: boolean, hasShipmentCard: boolean, hasShareLink: boolean): string {
  if (!text) return '';

  let cleaned = text;
  if (hasTableBlock) cleaned = stripMarkdownTables(cleaned);
  if (hasShipmentCard) cleaned = stripShipmentDetailBullets(cleaned);
  if (hasShareLink) cleaned = stripShareLinks(cleaned);

  cleaned = cleaned.replace(/^(?:Here is the shipment card|Here is the details for CN|Shipment details:|Details for shipment:)\s*:?\s*$/gim, '');
  cleaned = cleaned.replace(/```(?:[\w-]*)\n([\s\S]*?)```/g, (match, code) => {
    if (isAsciiChart(code)) return '';
    return match;
  });

  return cleaned.replace(/\n{3,}/g, '\n\n').trim();
}

const ChatMessage: React.FC<ChatMessageProps> = React.memo(({ role, content, blocks, isStreaming = false, toolStatus}) => {
  const isUser = role === 'user';
  const hasTableBlock = Boolean(blocks && blocks.some((b) => b.kind === 'table'));
  const hasShipmentCard = Boolean(blocks && blocks.some((b) => b.kind === 'shipment_card'));
  const hasShareLink = Boolean(blocks && blocks.some((b) => b.kind === 'share_link'));
  const displayContent = isUser
    ? content
    : cleanMessageContent(content, hasTableBlock, hasShipmentCard, hasShareLink);

  const hasBlocks = Boolean(blocks && blocks.length > 0);
  const shouldRenderBubble = Boolean(displayContent || (!hasBlocks && isStreaming) || isUser);

  return (
    <div className={cn('flex w-full mb-6 animate-in fade-in slide-in-from-bottom-2 duration-300', isUser ? 'justify-end' : 'justify-start')}>
      <div className={cn('flex gap-3', isUser ? 'max-w-[90%] sm:max-w-[80%] md:max-w-[75%] flex-row-reverse' : 'w-full flex-row')}>
        {/* Avatar */}
        <div className={cn('flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center shadow-sm', isUser ? 'bg-primary text-primary-foreground' : 'bg-white border text-blue-600')}>
          {isUser ? (
            <UserCircleIcon className="w-5 h-5" />
          ) : (
            <SparklesIcon className="w-5 h-5" />
          )}
        </div>

        {/* Message Content & Blocks */}
        <div className="flex flex-col gap-2 min-w-0 flex-1">
          {shouldRenderBubble && (
            <div
              className={cn(
                'px-4 py-3 rounded-2xl shadow-sm border text-sm leading-relaxed overflow-hidden',
                isUser
                  ? 'bg-primary text-primary-foreground rounded-tr-none border-primary/20'
                  : 'bg-white text-gray-800 rounded-tl-none border-gray-100'
              )}
            >
              {isUser ? (
                <div className="whitespace-pre-wrap font-sans">{content}</div>
              ) : !displayContent && isStreaming ? (
                <LoadingState variant="Dots" label={toolStatus || "Thinking…"} />
              ) : (
                <div className="markdown-body">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    disallowedElements={['img']}
                    components={{
                      p: ({ node, ...props }) => <p className="mb-2 last:mb-0" {...props} />,
                      ul: ({ node, ...props }) => <ul className="list-disc pl-4 mb-2 space-y-1" {...props} />,
                      ol: ({ node, ...props }) => <ol className="list-decimal pl-4 mb-2 space-y-1" {...props} />,
                      li: ({ node, ...props }) => <li className="" {...props} />,
                      a: ({ node, children, href, ...props }: any) => {
                        if (hasShareLink && (href?.includes('/shipments/track/public') || href?.includes('/share') || href?.includes('token='))) {
                          return null;
                        }
                        return (
                          <a className="text-blue-600 hover:underline font-medium" target="_blank" rel="noreferrer" href={href} {...props}>
                            {children}
                          </a>
                        );
                      },
                      code: ({ node, className, children, ...props }: any) => {
                        const match = /language-(\w+)/.exec(className || '');
                        return match ? (
                          <code className="block bg-gray-900 text-gray-100 p-2 rounded-md my-2 text-xs overflow-x-auto" {...props}>
                            {children}
                          </code>
                        ) : (
                          <code className="bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded text-xs font-mono border" {...props}>
                            {children}
                          </code>
                        );
                      },
                      blockquote: ({ node, ...props }) => (
                        <blockquote className="border-l-4 border-blue-200 pl-4 py-1 my-2 bg-blue-50/50 rounded-r italic text-gray-600" {...props} />
                      ),
                      table: ({ node, ...props }) => {
                        if (hasTableBlock) return null;
                        return (
                          <div className="overflow-x-auto my-4 rounded-lg border">
                            <table className="w-full text-left text-sm" {...props} />
                          </div>
                        );
                      },
                      thead: ({ node, ...props }) => <thead className="bg-gray-50 border-b" {...props} />,
                      th: ({ node, ...props }) => <th className="px-4 py-2 font-semibold text-gray-700" {...props} />,
                      td: ({ node, ...props }) => <td className="px-4 py-2 border-b last:border-0 border-gray-100" {...props} />,
                    }}
                  >
                    {displayContent}
                  </ReactMarkdown>
                  {isStreaming && (
                    <span className="inline-block w-1.5 h-3.5 ml-0.5 bg-blue-600 align-middle animate-pulse" />
                  )}
                </div>
              )}
            </div>
          )}

          {!isUser && hasBlocks && (
            <BlockList blocks={blocks} />
          )}

          {!isUser && isStreaming && toolStatus && Boolean(displayContent) && (
            <div className="px-1 py-1">
              <LoadingState variant="Dots" label={toolStatus} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

ChatMessage.displayName = 'ChatMessage';

export default ChatMessage;
